import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopMenu from '../TopMenu';
import './PuppeteerTemplateSelector.css';

const TEMPLATES = [
  {
    id: 'modern',
    name: 'Modern',
    color: '#1a5f7a',
    description: 'Clean sidebar layout with teal accents',
    category: ['recommended', 'all'],
    thumbnail: '/assets/templates/modern.webp',
  },
  {
    id: 'modern-teal-sidebar',
    name: 'Teal Sidebar',
    color: '#0d7377',
    description: 'Bold teal header with skill bars',
    category: ['recommended', 'all'],
    thumbnail: '/assets/templates/teal.webp',
  },
  {
    id: 'classic',
    name: 'Classic',
    color: '#222222',
    description: 'Traditional serif font layout',
    category: ['recommended', 'all'],
    thumbnail: '/assets/templates/old.webp',
  },
];

const TABS = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'new', label: 'New' },
  { key: 'all', label: 'All' },
];

const PuppeteerTemplateSelector = ({ theme, toggleTheme, onBack, experience, industry }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('recommended');
  const [selectedId, setSelectedId] = useState(TEMPLATES[0]?.id || '');
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);

  const filtered = TEMPLATES.filter((t) => t.category.includes(activeTab));
  const selected = TEMPLATES.find((t) => t.id === selectedId);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [filtered]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  const handleSelect = (templateId) => {
    navigate(`/resume-builder-v2/editor/${templateId}`);
  };

  const handleSkip = () => {
    navigate(`/resume-builder-v2/editor/${selectedId}`);
  };

  const isDark = theme === 'dark';

  return (
    <div className={`pts-page ${isDark ? 'pts-dark' : 'pts-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div className="pts-content">
        {/* Header */}
        <div className="pts-header">
          <h1>
            {industry
              ? `These are the templates we recommend for ${industry}`
              : 'Choose Your Resume Template'}
          </h1>
          <p className="pts-subtitle">You can always change templates later.</p>
        </div>

        {/* Tabs */}
        <div className="pts-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`pts-tab ${activeTab === tab.key ? 'pts-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Template carousel */}
        <div className="pts-carousel-wrapper">
          {canScrollLeft && (
            <button className="pts-arrow pts-arrow-left" onClick={() => scroll(-1)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}

          <div className="pts-carousel" ref={scrollRef} onScroll={checkScroll}>
            {filtered.map((tpl) => {
              const isActive = selectedId === tpl.id;
              return (
                <div
                  key={tpl.id}
                  className={`pts-card ${isActive ? 'pts-card-selected' : ''}`}
                  onClick={() => { setSelectedId(tpl.id); setPreviewTpl(tpl); }}
                >
                  <div className="pts-preview">
                    <img
                      src={tpl.thumbnail}
                      alt={tpl.name}
                      className="pts-preview-img"
                    />
                  </div>
                  {isActive && (
                    <div className="pts-check-badge" style={{ background: tpl.color }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {canScrollRight && (
            <button className="pts-arrow pts-arrow-right" onClick={() => scroll(1)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}
        </div>

        {/* See all templates link */}
        <div className="pts-see-all">
          <button className="pts-see-all-btn" onClick={() => setActiveTab('all')}>
            See all templates
          </button>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="pts-bottom-bar">
        <button className="pts-skip-btn" onClick={onBack || handleSkip}>
          Skip for now
        </button>
        <button
          className="pts-choose-btn"
          style={{ background: selected?.color || '#1a5f7a' }}
          onClick={() => handleSelect(selectedId)}
        >
          Choose this template
        </button>
      </div>

      {/* Lightbox preview modal */}
      {previewTpl && (
        <div className="pts-lightbox" onClick={() => setPreviewTpl(null)}>
          <div className="pts-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="pts-lightbox-close" onClick={() => setPreviewTpl(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <div className="pts-lightbox-img-wrap">
              <img src={previewTpl.thumbnail} alt={previewTpl.name} className="pts-lightbox-img" />
            </div>
            <div className="pts-lightbox-footer">
              <span className="pts-lightbox-name">{previewTpl.name}</span>
              <button
                className="pts-choose-btn"
                style={{ background: previewTpl.color }}
                onClick={() => handleSelect(previewTpl.id)}
              >
                Choose this template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PuppeteerTemplateSelector;
